import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Activity, RefreshCw, Calendar, TrendingUp, CheckCircle2, AlertCircle, FileSpreadsheet, Users } from "lucide-react";
import { PageShell, PageHeader, StatCard, StatGrid, ModernTable, ActionButton } from "@/components/ds";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(start), to: iso(end) };
}

const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);

export default function DashboardServices() {
  const [range, setRange] = useState(defaultRange);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dashboards/services?from_date=${range.from}&to_date=${range.to}`);
      setData(r.data);
    } catch (e) {
      toast.error("Erreur de chargement du tableau de bord Services");
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const rows = data?.rows || [];
  const chart = data?.chart_clients || [];

  const genRate = useMemo(() => pct(totals.generation_rate), [totals.generation_rate]);
  const complRate = useMemo(() => pct(totals.completion_rate), [totals.completion_rate]);

  return (
    <PageShell testId="dashboard-services">
      <PageHeader
        eyebrow="ANALYTICS"
        title="Dashboard Services"
        subtitle="Services planifiés vs missions générées et complétées — par ligne et par client."
        actions={
          <div className="flex items-center gap-2" data-testid="dashboard-services-controls">
            <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Du</label>
            <input type="date" value={range.from}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              className="border border-zinc-900 px-2 py-1 text-sm font-mono"
              data-testid="dashboard-services-from"
            />
            <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">au</label>
            <input type="date" value={range.to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="border border-zinc-900 px-2 py-1 text-sm font-mono"
              data-testid="dashboard-services-to"
            />
            <ActionButton onClick={load} icon={RefreshCw} testId="dashboard-services-refresh" disabled={loading}>
              Rafraîchir
            </ActionButton>
          </div>
        }
      />

      <StatGrid cols={4} testId="dashboard-services-kpis">
        <StatCard icon={FileSpreadsheet} label="LIGNES" value={totals.lines ?? 0}
          hint={`${totals.clients || 0} clients`} testId="kpi-svc-lines"
        />
        <StatCard icon={Calendar} label="PLANIFIÉS" value={totals.planned ?? 0}
          accent testId="kpi-svc-planned"
        />
        <StatCard icon={Activity} label="MISSIONS" value={totals.missions ?? 0}
          hint={`${genRate} générées`} testId="kpi-svc-missions"
        />
        <StatCard icon={CheckCircle2} label="TERMINÉES" value={totals.completed ?? 0}
          hint={complRate} tone="success" testId="kpi-svc-completed"
        />
        <StatCard icon={TrendingUp} label="EN COURS" value={totals.pending ?? 0} testId="kpi-svc-pending" />
        <StatCard icon={AlertCircle} label="SANS AFFECT." value={totals.pending_assignment ?? 0}
          tone={(totals.pending_assignment || 0) > 0 ? "warning" : "default"}
          testId="kpi-svc-pending-assignment"
        />
        <StatCard icon={Users} label="CLIENTS" value={totals.clients ?? 0} testId="kpi-svc-clients" />
        <StatCard icon={TrendingUp} label="TAUX GÉNÉRATION" value={genRate}
          tone={(totals.generation_rate || 0) >= 0.9 ? "success" : "warning"}
          testId="kpi-svc-gen-rate"
        />
      </StatGrid>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 border border-zinc-900 bg-white p-5" data-testid="dashboard-services-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-black uppercase text-lg tracking-tight">Top 10 — Services par client</h3>
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{range.from} → {range.to}</span>
          </div>
          {chart.length === 0 ? (
            <div className="text-center text-zinc-500 text-xs py-12">Aucun service planifié sur la période</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "#FFF7CC" }} />
                <Bar dataKey="planned" fill="#18181b" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-zinc-900 bg-[#FFC60A] p-5" data-testid="dashboard-services-completion">
          <h3 className="font-display font-black uppercase text-lg tracking-tight mb-4">Production</h3>
          <div className="space-y-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">Taux génération</div>
              <div className="font-display font-black text-5xl leading-none">{genRate}</div>
              <div className="text-xs mt-1">{totals.missions || 0} / {totals.planned || 0} services</div>
            </div>
            <div className="pt-4 border-t-2 border-zinc-900">
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">Taux complétion</div>
              <div className="font-display font-black text-5xl leading-none">{complRate}</div>
              <div className="text-xs mt-1">{totals.completed || 0} terminées / {totals.missions || 0} missions</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="font-display font-black uppercase text-lg tracking-tight mb-3">Détail par ligne</h3>
        <ModernTable
          testId="dashboard-services-table"
          stickyHeader
          columns={[
            { key: "service_code", label: "Code" },
            { key: "client_name", label: "Client" },
            { key: "planned", label: "Planifiés", align: "right" },
            { key: "missions", label: "Missions", align: "right" },
            { key: "completed", label: "Terminées", align: "right" },
            { key: "pending", label: "Pending", align: "right" },
            { key: "pending_assignment", label: "Sans affect.", align: "right" },
            { key: "generation_rate", label: "% Génér.", align: "right" },
            { key: "completion_rate", label: "% Compl.", align: "right" },
            { key: "first_date", label: "Période" },
          ]}
          rows={rows}
          rowKey={(r) => `${r.client_name}-${r.service_code}`}
          renderCell={(row, col) => {
            switch (col.key) {
              case "service_code":
                return (
                  <span className="inline-block px-2 py-0.5 bg-zinc-900 text-[#FFC60A] font-display font-black text-xs"
                    data-testid={`dashboard-svc-row-${row.service_code}`}>
                    {row.service_code}
                  </span>
                );
              case "generation_rate": return pct(row.generation_rate);
              case "completion_rate": return pct(row.completion_rate);
              case "first_date":
                return row.first_date && row.last_date
                  ? <span className="text-xs text-zinc-600">{row.first_date} → {row.last_date}</span>
                  : <span className="text-zinc-400">—</span>;
              case "pending_assignment":
                return row.pending_assignment > 0
                  ? <span className="text-orange-700 font-bold">{row.pending_assignment}</span>
                  : row.pending_assignment;
              default:
                return row[col.key];
            }
          }}
          emptyMessage={loading ? "Chargement…" : "Aucun service planifié sur la période."}
        />
      </div>
    </PageShell>
  );
}
